import React, { useState, useEffect, useRef, useCallback } from 'react';
import './Card.css';
import { useUser } from '../../auth/AuthPage/UserContext';
import { useNavigate } from 'react-router-dom';
import CardInfoOverlay from './CardInfoOverlay';
import { useImageSource } from '../../../contexts/ImageSourceContext';
import { useBinder } from '../../../contexts/BinderContext';
import { getSetName } from '../../../utils/sets';
import Icon from '../Icon/Icon';

function UnifiedCard({
    card,
    entry,
    displayMode = 'standard',
    setName,
    forceShowInfo = false,
    onClose,
    overlayOnly = false,

    // Mode determines the action button behavior
    mode = 'standard', // 'standard' | 'collection' | 'binder' | 'trade' | 'request'

    // Callbacks for different modes
    onAddToCollection,
    onAddToTrade,
    onAddToTradeWithFinish,
    onUpdateEntry,
    onDeleteEntry,

    // Additional props
    prices: propPrices,
    priceLoading: propPriceLoading,
    showExtendedInfo = false,
    readOnly = false,
    isHighlighted = false,
    toggleOn,
    activeTab,
    className = '',
    entryType = 'have',
    onInfoClick = null,  // If provided, parent handles the info overlay
    addDisabled = false  // Grey out the add button (e.g. owned-quantity cap reached)
}) {
    const [showInfo, setShowInfo] = useState(false);
    const [imageError, setImageError] = useState(false);
    const [showBack, setShowBack] = useState(false);
    const [prices, setPrices] = useState({});
    const [pricesLoading, setPricesLoading] = useState(false);
    const [setDisplayName, setSetDisplayName] = useState(setName || card?.setCode || '');
    const [fullCardData, setFullCardData] = useState(null);
    const [cardLoading, setCardLoading] = useState(mode === 'request');

    const wrapperRef = useRef(null);
    const popoverRef = useRef(null);
    const { user } = useUser();
    const navigate = useNavigate();
    const { getImageUrl } = useImageSource();

    // Collection quick-add state (one-click +Have/+Want; finish picker
    // only opens when the card has multiple finishes)
    const binder = useBinder();
    const [quickAddType, setQuickAddType] = useState(null); // 'have'|'want' while finish picker open
    const [quickAddBusy, setQuickAddBusy] = useState(false);
    const [showLoginPopup, setShowLoginPopup] = useState(false);

    // Binder mode state
    const [binderEditPopoverOpen, setBinderEditPopoverOpen] = useState(false);
    const [binderEditLoading, setBinderEditLoading] = useState(false);
    const [binderEditError, setBinderEditError] = useState('');
    const [binderEditSuccess, setBinderEditSuccess] = useState(false);

    // Trade mode state
    const [tradePopoverOpen, setTradePopoverOpen] = useState(false);
    const [tradeSelectedFinish, setTradeSelectedFinish] = useState(card?.finishes?.[0] || '');

    // Use prop prices if provided, otherwise fetch
    const finalPrices = propPrices || prices;
    const finalPricesLoading = propPriceLoading || pricesLoading;

    // Process embedded prices from card data (no API call needed!)
    // Card data already contains tcgplayer_prices, cardkingdom_prices, min_prices arrays
    const processEmbeddedPrices = useCallback((cardData) => {
        if (!cardData) return {};

        const pricesByFinish = {};

        // Parse finishes array
        let finishes = [];
        if (Array.isArray(cardData.finishes)) {
            finishes = cardData.finishes;
        } else if (typeof cardData.finishes === 'string') {
            try {
                finishes = JSON.parse(cardData.finishes);
            } catch (e) {
                finishes = [];
            }
        }

        if (finishes.length === 0) return {};

        // Parse price arrays
        const parseArray = (val) => {
            if (Array.isArray(val)) return val;
            if (typeof val === 'string') {
                try {
                    return JSON.parse(val);
                } catch (e) {
                    return [];
                }
            }
            return [];
        };

        const tcgplayerPrices = parseArray(cardData.tcgplayer_prices);
        const cardkingdomPrices = parseArray(cardData.cardkingdom_prices);
        const minPrices = parseArray(cardData.min_prices);

        // Map prices to finishes
        finishes.forEach((finish, index) => {
            const tcgPrice = tcgplayerPrices[index] !== undefined ? parseFloat(tcgplayerPrices[index]) : null;
            const ckPrice = cardkingdomPrices[index] !== undefined ? parseFloat(cardkingdomPrices[index]) : null;
            const minPrice = minPrices[index] !== undefined ? parseFloat(minPrices[index]) : null;

            // Validate prices
            const validTcg = tcgPrice !== null && !isNaN(tcgPrice) && tcgPrice > 0 ? tcgPrice : null;
            const validCk = ckPrice !== null && !isNaN(ckPrice) && ckPrice > 0 ? ckPrice : null;
            const validMin = minPrice !== null && !isNaN(minPrice) && minPrice > 0 ? minPrice : null;

            // Find best deal (lowest price) if min_price not available
            let usdPrice = validMin;
            let bestSource = null;

            if (!usdPrice) {
                const sources = [
                    { price: validTcg, source: 'tcgplayer' },
                    { price: validCk, source: 'cardkingdom' }
                ];
                for (const { price, source } of sources) {
                    if (price !== null && (usdPrice === null || price < usdPrice)) {
                        usdPrice = price;
                        bestSource = source;
                    }
                }
            } else {
                // Determine best source based on which matches min_price
                if (validTcg === validMin) bestSource = 'tcgplayer';
                else if (validCk === validMin) bestSource = 'cardkingdom';
            }

            pricesByFinish[finish] = {
                setCode: cardData.setCode,
                tcgplayer_price: validTcg !== null ? validTcg.toFixed(2) : null,
                cardkingdom_price: validCk !== null ? validCk.toFixed(2) : null,
                usd_price: usdPrice !== null ? usdPrice.toFixed(2) : null,
                best_source: bestSource
            };
        });

        return pricesByFinish;
    }, []);

    // Fetch prices from API (fallback - only used when card doesn't have embedded prices)
    const fetchPrices = useCallback(async () => {
        if (!card?.uuid) return;

        // First, try to use embedded prices from card data (no API call!)
        if (card.tcgplayer_prices || card.cardkingdom_prices || card.min_prices) {
            const embeddedPrices = processEmbeddedPrices(card);
            if (Object.keys(embeddedPrices).length > 0) {
                setPrices(embeddedPrices);
                return;
            }
        }

        // Fallback: fetch from API only if no embedded prices
        try {
            setPricesLoading(true);
            const url = card.setCode
                ? `/api/card-prices/${card.uuid}?setCode=${card.setCode}`
                : `/api/card-prices/${card.uuid}`;
            const response = await fetch(url);
            if (response.ok) {
                const priceData = await response.json();
                setPrices(priceData);
            }
        } catch (error) {
            console.error('Error fetching prices:', error);
        } finally {
            setPricesLoading(false);
        }
    }, [card?.uuid, card?.setCode, card?.tcgplayer_prices, card?.cardkingdom_prices, card?.min_prices, processEmbeddedPrices]);

    // Fetch full card data (for request mode)
    const fetchFullCardData = useCallback(async () => {
        if (!card?.uuid || !card?.setCode) return;
        try {
            setCardLoading(true);
            const response = await fetch(`/api/cards/${card.setCode}/${card.uuid}`);
            if (response.ok) {
                const cardData = await response.json();
                setFullCardData(cardData);
            }
        } catch (error) {
            console.error('Error fetching card data:', error);
        } finally {
            setCardLoading(false);
        }
    }, [card?.uuid, card?.setCode]);

    // Effects
    useEffect(() => {
        if (forceShowInfo) setShowInfo(true);
    }, [forceShowInfo]);

    useEffect(() => {
        if (card && card.uuid && !propPrices) {
            fetchPrices();
        }
    }, [card, card?.uuid, propPrices, fetchPrices]);

    // Set name: use the one the parent already knows; otherwise look it
    // up in the shared, fetched-once set list. (The old version fetched
    // the whole list per card and, via a dependency loop, kept doing so.)
    useEffect(() => {
        if (setName) { setSetDisplayName(setName); return; }
        const code = entry?.setcode || card?.setCode;
        if (!code) return;
        let alive = true;
        getSetName(code).then(name => { if (alive) setSetDisplayName(name); });
        return () => { alive = false; };
    }, [setName, entry?.setcode, card?.setCode]);

    useEffect(() => {
        if (mode === 'request' && card && card.uuid && card.setCode) {
            fetchFullCardData();
        }
    }, [mode, card, fetchFullCardData]);

    // Binder mode: show confirmation for 1s
    useEffect(() => {
        let timer;
        if (binderEditSuccess) {
            timer = setTimeout(() => setBinderEditSuccess(false), 1000);
        }
        return () => clearTimeout(timer);
    }, [binderEditSuccess]);

    // Close popovers on outside click
    useEffect(() => {
        function handleClickOutside(event) {
            if (popoverRef.current && !popoverRef.current.contains(event.target)) {
                setQuickAddType(null);
                setTradePopoverOpen(false);
            }
        }
        if (quickAddType || tradePopoverOpen) {
            document.addEventListener('mousedown', handleClickOutside);
        }
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [quickAddType, tradePopoverOpen]);

    // Close popovers on mouse leave
    useEffect(() => {
        function handleMouseLeave(event) {
            if (wrapperRef.current && !wrapperRef.current.contains(event.relatedTarget)) {
                setQuickAddType(null);
                setBinderEditPopoverOpen(false);
                setTradePopoverOpen(false);
            }
        }
        const node = wrapperRef.current;
        if (node) {
            node.addEventListener('mouseleave', handleMouseLeave);
        }
        return () => {
            if (node) node.removeEventListener('mouseleave', handleMouseLeave);
        };
    }, []);

    // Collection mode: one-click add to haves/wants.
    // Have: instant when the card has one finish, tiny finish picker when
    // it has several. Want: instant as "any printing" (best for matching).
    const cardFinishes = Array.isArray(card?.finishes) ? card.finishes : [];

    const performQuickAdd = async (type, finish) => {
        setQuickAddType(null);
        if (onAddToCollection) {
            try { await onAddToCollection(type, card, finish, 1); } catch { }
            return;
        }
        if (!binder || quickAddBusy) return;
        setQuickAddBusy(true);
        try { await binder.quickAdd(type, card, finish); } catch { }
        setQuickAddBusy(false);
    };

    const handleQuickAdd = (type) => {
        if (!user) {
            setShowLoginPopup(true);
            return;
        }
        // Want always offers a choice: "Any printing" or a specific finish.
        if (type === 'want') {
            setQuickAddType('want');
            return;
        }
        if (cardFinishes.length > 1) {
            setQuickAddType('have');
            return;
        }
        performQuickAdd('have', cardFinishes[0] || 'nonfoil');
    };

    // Binder mode: set quantity directly (used by the +/- stepper)
    const handleBinderSetQty = async (newQty) => {
        if (!user || binderEditLoading || newQty < 1) return;
        setBinderEditLoading(true);
        setBinderEditError('');
        try {
            const endpoint = entryType === 'want' ? '/api/wants' : '/api/haves';
            const res = await fetch(endpoint, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify({
                    uuid: card.uuid,
                    quantity: newQty,
                    finish: entry.finish,
                    game: 'magic'
                })
            });
            if (!res.ok) throw new Error('Failed to update quantity');
            if (onUpdateEntry) onUpdateEntry(card.uuid, entry.finish, newQty);
            if (binder) binder.refresh();
        } catch (err) {
            setBinderEditError('Failed to update quantity');
        } finally {
            setBinderEditLoading(false);
        }
    };

    // Binder mode: Delete card
    const handleBinderDelete = async () => {
        if (!user) return;
        setBinderEditLoading(true);
        setBinderEditError('');
        try {
            const endpoint = entryType === 'want' ? '/api/wants' : '/api/haves';
            const res = await fetch(endpoint, {
                method: 'DELETE',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify({
                    uuid: card.uuid,
                    finish: entry.finish,
                    game: 'magic'
                })
            });
            if (!res.ok) throw new Error('Failed to delete card');
            setBinderEditSuccess('deleted');
            setBinderEditPopoverOpen(false);
            if (onDeleteEntry) onDeleteEntry(card.uuid, entry.finish);
            if (binder) binder.refresh();
        } catch (err) {
            setBinderEditError('Failed to delete card');
        } finally {
            setBinderEditLoading(false);
        }
    };

    if (!card) {
        return (
            <div className="card-container placeholder">
                <div className="card-image-placeholder">No Card Data</div>
            </div>
        );
    }

    // Use fullCardData if available (request mode)
    const displayCard = fullCardData || card;

    const handleInfoClick = (e) => {
        e.stopPropagation();
        setShowBack(false);
        // If parent provides onInfoClick, call it instead of showing internal overlay
        if (onInfoClick) {
            onInfoClick();
        } else {
            setShowInfo(true);
        }
    };

    const closeInfo = () => {
        setShowInfo(false);
        if (onClose) onClose();
    };

    const handleImageError = () => {
        setImageError(true);
    };

    // Use centralized image source context for consistent image routing
    const imagePath = getImageUrl(displayCard, showBack);

    const containerClass = `card-container ${displayMode === 'compact' ? 'card-container-compact' : ''} ${isHighlighted ? 'card-highlighted' : ''} ${className}`;

    // Single price for the card face: the entry's finish price when this
    // is a binder/trade entry, otherwise the lowest across finishes.
    // Full per-finish breakdown lives in the info overlay.
    const facePrice = (() => {
        if (entry?.finish && finalPrices[entry.finish]?.usd_price) {
            return finalPrices[entry.finish].usd_price;
        }
        const all = Object.values(finalPrices)
            .map(p => parseFloat(p?.usd_price))
            .filter(v => !isNaN(v) && v > 0);
        if (all.length > 0) return Math.min(...all).toFixed(2);
        const sp = parseFloat(displayCard.sortPrice ?? displayCard.min_price);
        return !isNaN(sp) && sp > 0 ? sp.toFixed(2) : null;
    })();

    // Ownership annotations for search results (cards that aren't
    // themselves binder entries)
    const ownedCount = binder && user && !entry ? binder.ownedQty(displayCard.uuid) : 0;
    const wantedCount = binder && user && !entry ? binder.wantedQty(displayCard.uuid) : 0;

    // Render action button based on mode
    const renderActionButton = () => {
        // Collection mode: one-click +Have / +Want
        if (mode === 'collection' && !showInfo && !overlayOnly && !quickAddType) {
            return (
                <div className="quick-add-actions">
                    <button
                        className="quick-add-btn quick-add-have"
                        disabled={quickAddBusy}
                        onClick={e => { e.stopPropagation(); handleQuickAdd('have'); }}
                    >
                        + Have
                    </button>
                    <button
                        className="quick-add-btn quick-add-want"
                        disabled={quickAddBusy}
                        onClick={e => { e.stopPropagation(); handleQuickAdd('want'); }}
                    >
                        + Want
                    </button>
                </div>
            );
        }

        // Binder mode: +/- quantity stepper (only when viewing binder, not when searching)
        // toggleOn === true means "My Cards" mode, toggleOn === false means "All Cards" search mode
        // toggleOn === undefined means not in dual mode (just viewing binder)
        if (mode === 'binder' && !readOnly && !binderEditPopoverOpen && (toggleOn === true || toggleOn === undefined)) {
            const qty = entry?.quantity || 1;
            return (
                <div className="qty-stepper" onClick={e => e.stopPropagation()}>
                    <button
                        className="qty-stepper-btn"
                        disabled={binderEditLoading}
                        aria-label={qty <= 1 ? 'Remove card' : 'Decrease quantity'}
                        onClick={() => {
                            if (qty <= 1) {
                                setBinderEditPopoverOpen(true); // confirm removal
                                setBinderEditError('');
                            } else {
                                handleBinderSetQty(qty - 1);
                            }
                        }}
                    >
                        −
                    </button>
                    <span className="qty-stepper-count">×{qty}</span>
                    <button
                        className="qty-stepper-btn"
                        disabled={binderEditLoading}
                        aria-label="Increase quantity"
                        onClick={() => handleBinderSetQty(qty + 1)}
                    >
                        +
                    </button>
                </div>
            );
        }

        // Trade mode: Add to Trade button
        // If card has an entry with finish (from collection), use onAddToTrade directly
        // Otherwise show finish selection popover
        if (mode === 'trade' && onAddToTrade && (toggleOn || entry?.finish)) {
            return (
                <button
                    className="add-to-trade-button"
                    disabled={addDisabled}
                    title={addDisabled ? 'Quantity limit reached. They only own so many' : 'Add to Trade'}
                    onClick={e => {
                        e.stopPropagation();
                        onAddToTrade(card, entry);
                    }}
                    aria-label="Add to Trade"
                >
                    +
                </button>
            );
        }

        if (mode === 'trade' && onAddToTradeWithFinish && !toggleOn && !entry?.finish && !tradePopoverOpen) {
            return (
                <button
                    className="add-to-trade-button"
                    onClick={e => {
                        e.stopPropagation();
                        setTradePopoverOpen(true);
                        setTradeSelectedFinish(card?.finishes?.[0] || '');
                    }}
                    aria-label="Add to Trade"
                >
                    +
                </button>
            );
        }

        // MakeRequest, SearchCollections, SearchRequests, TradeSearch modes: Add to Trade button
        if ((mode === 'makeRequest' || mode === 'searchCollections' || mode === 'searchRequests' || mode === 'tradeSearch') && onAddToTrade && toggleOn) {
            return (
                <button
                    className="add-to-trade-button"
                    disabled={addDisabled}
                    title={addDisabled ? 'Quantity limit reached' : 'Add to Trade'}
                    onClick={e => {
                        e.stopPropagation();
                        onAddToTrade(card, entry);
                    }}
                    aria-label="Add to Trade"
                >
                    +
                </button>
            );
        }

        if ((mode === 'makeRequest' || mode === 'searchCollections' || mode === 'searchRequests' || mode === 'tradeSearch' || mode === 'binder') && onAddToTradeWithFinish && !toggleOn && !tradePopoverOpen) {
            return (
                <button
                    className="add-to-trade-button"
                    onClick={e => {
                        e.stopPropagation();
                        setTradePopoverOpen(true);
                        setTradeSelectedFinish(card?.finishes?.[0] || '');
                    }}
                    aria-label="Add to Trade"
                >
                    +
                </button>
            );
        }

        return null;
    };

    // Render popover based on mode
    const renderPopover = () => {
        // Collection mode: finish picker (only when adding a Have on a
        // card with multiple finishes - one tap adds it)
        if (mode === 'collection' && quickAddType) {
            return (
                <div className="add-popover popover-animate" ref={popoverRef}>
                    <div className="finish-pick-title">{quickAddType === 'want' ? 'Which printing?' : 'Which finish?'}</div>
                    <div className="finish-pick-list">
                        {quickAddType === 'want' && (
                            <button
                                className="finish-pick-btn finish-pick-any"
                                onClick={e => { e.stopPropagation(); performQuickAdd('want', 'any'); }}
                            >
                                Any printing
                            </button>
                        )}
                        {cardFinishes.map(f => (
                            <button
                                key={f}
                                className="finish-pick-btn"
                                onClick={e => { e.stopPropagation(); performQuickAdd(quickAddType, f); }}
                            >
                                {f}
                            </button>
                        ))}
                    </div>
                    <button className="add-cancel-btn" onClick={e => { e.stopPropagation(); setQuickAddType(null); }}>Cancel</button>
                </div>
            );
        }

        // Binder mode: confirm removing the last copy
        if (mode === 'binder' && binderEditPopoverOpen) {
            return (
                <div className="add-popover popover-animate binder-edit-popover" ref={popoverRef}>
                    <div className="finish-pick-title">Remove from {entryType === 'want' ? 'wishlist' : 'collection'}?</div>
                    <div className="add-details-actions binder-edit-actions">
                        <button className="add-cancel-btn delete-btn" onClick={handleBinderDelete} disabled={binderEditLoading}>Remove</button>
                        <button className="add-cancel-btn" onClick={() => setBinderEditPopoverOpen(false)} disabled={binderEditLoading}>Cancel</button>
                    </div>
                    {binderEditError && <div className="popover-error">{binderEditError}</div>}
                </div>
            );
        }

        // Trade mode popover (for finish selection) - also handles makeRequest, searchCollections, searchRequests, tradeSearch, binder (in search mode)
        if ((mode === 'trade' || mode === 'makeRequest' || mode === 'searchCollections' || mode === 'searchRequests' || mode === 'tradeSearch' || mode === 'binder') && tradePopoverOpen) {
            return (
                <div className="add-popover popover-animate" ref={popoverRef}>
                    <div className="add-details-form">
                        <div className="add-details-row">
                            <label>Finish:</label>
                            <select
                                value={tradeSelectedFinish}
                                onChange={e => setTradeSelectedFinish(e.target.value)}
                                className="add-finish-select"
                            >
                                {(activeTab === 'want' || entryType === 'want') && <option value="any">Any printing</option>}
                                {card.finishes && card.finishes.map(f => (
                                    <option key={f} value={f}>{f}</option>
                                ))}
                            </select>
                        </div>
                        <div className="add-details-actions">
                            <button className="add-confirm-btn" onClick={() => {
                                onAddToTradeWithFinish(card, tradeSelectedFinish);
                                setTradePopoverOpen(false);
                            }}>Confirm</button>
                            <button className="add-cancel-btn" onClick={() => setTradePopoverOpen(false)}>Cancel</button>
                        </div>
                    </div>
                </div>
            );
        }

        return null;
    };

    // Render confirmation/success messages
    const renderFeedback = () => {
        if (mode === 'binder' && binderEditSuccess === 'updated') {
            return <div className="add-confirmation-feedback">Updated!</div>;
        }
        if (mode === 'binder' && binderEditSuccess === 'deleted') {
            return <div className="add-confirmation-feedback">Deleted!</div>;
        }
        return null;
    };

    const loginPopupEl = showLoginPopup && (
        <div className="login-required-popup-overlay" onClick={() => setShowLoginPopup(false)}>
            <div className="login-required-popup" onClick={e => e.stopPropagation()}>
                <button className="login-required-popup-close" onClick={() => setShowLoginPopup(false)} aria-label="Close">×</button>
                <div className="login-required-popup-message">
                    You need to log in to add cards to your binders.
                </div>
                <button className="login-required-popup-auth-btn" onClick={() => navigate('/auth')}>
                    Log In / Sign Up
                </button>
            </div>
        </div>
    );

    // ---------- LIST VIEW ----------
    // Same data and actions as the grid card, laid out as a row.
    if (displayMode === 'list') {
        const listActions = () => {
            if (mode === 'collection' && !overlayOnly) {
                return (
                    <>
                        <button className="quick-add-btn quick-add-have" disabled={quickAddBusy}
                            onClick={e => { e.stopPropagation(); handleQuickAdd('have'); }}>+ Have</button>
                        <button className="quick-add-btn quick-add-want" disabled={quickAddBusy}
                            onClick={e => { e.stopPropagation(); handleQuickAdd('want'); }}>+ Want</button>
                    </>
                );
            }
            if (mode === 'binder' && !readOnly && (toggleOn === true || toggleOn === undefined)) {
                const qty = entry?.quantity || 1;
                return (
                    <div className="qty-stepper qty-stepper-inline" onClick={e => e.stopPropagation()}>
                        <button className="qty-stepper-btn" disabled={binderEditLoading}
                            aria-label={qty <= 1 ? 'Remove card' : 'Decrease quantity'}
                            onClick={() => {
                                if (qty <= 1) { setBinderEditPopoverOpen(true); setBinderEditError(''); }
                                else { handleBinderSetQty(qty - 1); }
                            }}>−</button>
                        <span className="qty-stepper-count">×{qty}</span>
                        <button className="qty-stepper-btn" disabled={binderEditLoading}
                            aria-label="Increase quantity"
                            onClick={() => handleBinderSetQty(qty + 1)}>+</button>
                    </div>
                );
            }
            const tradeModes = mode === 'makeRequest' || mode === 'searchCollections' || mode === 'searchRequests' || mode === 'tradeSearch';
            if ((mode === 'trade' && onAddToTrade && (toggleOn || entry?.finish)) || (tradeModes && onAddToTrade && toggleOn)) {
                return (
                    <button className="quick-add-btn quick-add-have" disabled={addDisabled}
                        title={addDisabled ? 'Quantity limit reached' : undefined}
                        onClick={e => { e.stopPropagation(); onAddToTrade(card, entry); }}>+ Add</button>
                );
            }
            if ((mode === 'trade' || tradeModes || mode === 'binder') && onAddToTradeWithFinish && !toggleOn && !entry?.finish) {
                return (
                    <button className="quick-add-btn quick-add-have"
                        onClick={e => { e.stopPropagation(); setTradePopoverOpen(true); setTradeSelectedFinish(card?.finishes?.[0] || ''); }}>+ Add</button>
                );
            }
            return null;
        };

        return (
            <>
                {loginPopupEl}
                <div className={`card-row ${isHighlighted ? 'card-highlighted' : ''} ${className}`}>
                    <button className="card-row-name" onClick={handleInfoClick} title="Card details">
                        <span className="card-row-name-text">{displayCard.name || 'Unknown Card'}</span>
                        {ownedCount > 0 && <span className="owned-badge inline">×{ownedCount}</span>}
                        {wantedCount > 0 && <span className="want-badge inline"><Icon name="heart" /></span>}
                        {entry?.quantity > 1 && mode !== 'binder' && <span className="owned-badge inline">×{entry.quantity}</span>}
                    </button>
                    <span className="card-row-meta">
                        {setDisplayName || displayCard.setCode}
                        {displayCard.rarity ? ` · ${displayCard.rarity}` : ''}
                        {entry?.finish ? ` · ${entry.finish}` : ''}
                    </span>
                    <span className="card-row-price">
                        {finalPricesLoading ? '…' : facePrice != null ? `$${facePrice}` : 'N/A'}
                    </span>
                    <div className="card-row-actions">
                        {listActions()}
                        {renderPopover()}
                    </div>
                </div>
                {!onInfoClick && (
                    <CardInfoOverlay
                        show={showInfo}
                        card={displayCard}
                        entry={entry}
                        prices={finalPrices}
                        setDisplayName={setDisplayName}
                        showExtendedInfo={showExtendedInfo}
                        imageError={imageError}
                        onClose={closeInfo}
                        onImageError={handleImageError}
                        quantityBadge={entry?.quantity ? `x${entry.quantity}` : null}
                    />
                )}
            </>
        );
    }

    return (
        <>
            {loginPopupEl}

            <div className={containerClass}>
                <div className="card-image-wrapper" ref={wrapperRef}>
                    {/* Backside toggle */}
                    {(displayCard.backside_image_path || displayCard.local_backside_path) && (
                        <button
                            className="backside-toggle"
                            onClick={e => {
                                e.stopPropagation();
                                setShowBack(b => !b);
                            }}
                            aria-label={showBack ? 'Show front side' : 'Show back side'}
                        >
                            ⇄
                        </button>
                    )}

                    {/* Quantity badge (trade mode; binder mode shows the stepper instead) */}
                    {entry?.quantity && mode === 'trade' && (
                        <div className="binder-quantity-badge">
                            x{entry.quantity}
                        </div>
                    )}

                    {/* Ownership annotations on search results */}
                    {ownedCount > 0 && (
                        <div className="owned-badge" title={`In your collection ×${ownedCount}`}>
                            ×{ownedCount}
                        </div>
                    )}
                    {wantedCount > 0 && (
                        <div className="want-badge" title="On your wishlist"><Icon name="heart" /></div>
                    )}

                    {/* Action button */}
                    {renderActionButton()}

                    {/* Popover */}
                    {renderPopover()}

                    {/* Feedback messages */}
                    {renderFeedback()}

                    {/* Info button */}
                    <button
                        className="info-button"
                        onClick={handleInfoClick}
                        aria-label="Card Information"
                    >
                        i
                    </button>

                    {/* Card image */}
                    {cardLoading ? (
                        <div className="card-image-placeholder">Loading...</div>
                    ) : imagePath && !imageError ? (
                        <img
                            src={imagePath}
                            alt={displayCard.name}
                            className="card-image card-image-clickable"
                            loading="lazy"
                            onError={handleImageError}
                            onClick={handleInfoClick}
                            title="Card details"
                        />
                    ) : (
                        <div className="card-image-placeholder">
                            {imageError ? 'Image Not Found' : 'No Image'}
                            <br />
                            <small style={{ fontSize: '0.7rem', opacity: 0.7 }}>
                                {displayCard.setCode} - {displayCard.name}
                            </small>
                        </div>
                    )}
                </div>

                {/* Card info: name, one price, muted set/rarity line */}
                <div className="card-info">
                    <h3 className="card-name">{displayCard.name || 'Unknown Card'}</h3>
                    <div className="card-face-price">
                        {finalPricesLoading ? '…' : facePrice != null ? `$${facePrice}` : 'N/A'}
                        {entry?.finish && entry.finish !== 'nonfoil' && (
                            <span className="card-face-finish">{entry.finish}</span>
                        )}
                    </div>
                    <div className="card-face-meta">
                        {setDisplayName || displayCard.setCode}
                        {displayCard.rarity ? ` · ${displayCard.rarity}` : ''}
                    </div>
                </div>
            </div>

            {/* Info overlay - only render if parent is NOT handling it via onInfoClick */}
            {!onInfoClick && (
                <CardInfoOverlay
                    show={showInfo}
                    card={displayCard}
                    entry={entry}
                    prices={finalPrices}
                    setDisplayName={setDisplayName}
                    showExtendedInfo={showExtendedInfo}
                    imageError={imageError}
                    onClose={closeInfo}
                    onImageError={handleImageError}
                    quantityBadge={entry?.quantity ? `x${entry.quantity}` : null}
                />
            )}
        </>
    );
}

// Wrap with React.memo to prevent unnecessary re-renders
// Only re-render if relevant props change
export default React.memo(UnifiedCard, (prevProps, nextProps) => {
    // Return true if we should NOT re-render (props are equal)
    return (
        prevProps.card?.uuid === nextProps.card?.uuid &&
        prevProps.card?.setCode === nextProps.card?.setCode &&
        prevProps.entry?.quantity === nextProps.entry?.quantity &&
        prevProps.entry?.finish === nextProps.entry?.finish &&
        prevProps.displayMode === nextProps.displayMode &&
        prevProps.mode === nextProps.mode &&
        prevProps.toggleOn === nextProps.toggleOn &&
        prevProps.activeTab === nextProps.activeTab &&
        prevProps.isHighlighted === nextProps.isHighlighted &&
        prevProps.readOnly === nextProps.readOnly &&
        prevProps.addDisabled === nextProps.addDisabled &&
        prevProps.onInfoClick === nextProps.onInfoClick  // Re-render if onInfoClick changes
    );
});
